<template>
  <div class="card-panel flex flex-wrap gap-y-4">
    <div class="w-full divide-y">
      <div>
        <div class="p-4 font-medium">
          {{ t('campaign_asset_set.send_set') }}
        </div>
        <div class="flex flex-wrap items-start gap-4 px-10 pb-4">
          <div class="w-fit pt-1.5">
            {{ t('campaign_asset_set.is_apply_sms_template') }} ：
          </div>
          <div class="flex flex-wrap items-center gap-y-4">
            <div class="flex w-full items-center">
              <radio
                v-model="isUseTemplate"
                name="check"
                value="yes"
                :disabled="props.disabled"
              >
                <template #text>
                  <div class="flex items-center gap-4">
                    <div>{{ t('yes') }}</div>
                    <selects
                      v-model="useTemplate"
                      :items="info.edmList"
                      :disabled="isUseTemplate !== 'yes' || !info.edmList?.length"
                      :placeholder="t('select_template')"
                      searchable
                      class="w-52"
                      @change="warnModal = true"
                    />
                  </div>
                </template>
              </radio>
            </div>
            <div class="'w-full flex items-center">
              <radio
                v-model="isUseTemplate"
                name="check"
                value="no"
                :text="t('no')"
                :disabled="props.disabled"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="flex flex-wrap gap-y-2 p-4">
        <div class="font-medium">
          {{ t('campaign.ai_recommend_keyword') }}
        </div>
        <div class="flex w-full gap-2 px-8 py-4">
          <div>{{ t('campaign.recommend_keyword') }}：</div>
          <div class="flex gap-2">
            <div
              v-for="(word, wordIdx) in props.keyword"
              :key="`word-${wordIdx}`"
              class="text-cdp-main-primary-dark"
            >
              {{ word.text }}
              <span v-show="wordIdx + 1 !== props.keyword?.length">、</span>
            </div>
            <span v-show="!props.keyword.length">
              {{ t('none') }}
            </span>
          </div>
        </div>
      </div>
      <div class="flex flex-wrap gap-y-2 p-4">
        <div class="w-full lg:w-2/3 xl:w-1/2">
          <div class="font-medium">
            {{ t('campaign_asset_set.sms_context_editor') }}
          </div>
          <v-form
            ref="smsForm"
            v-slot="{ errors }"
            class="flex flex-wrap gap-y-4 px-8 py-4"
          >
            <div class="flex w-full flex-wrap">
              <div class="w-full">
                <div class="pb-1 text-xs">
                  {{ t('campaign_asset_set.sms_context') }} ：
                </div>
                <div class="w-full">
                  <field
                    v-slot="{ field }"
                    v-model="info.data.text"
                    :name="t('campaign_asset_set.sms_context')"
                    rules="min:1"
                  >
                    <textarea
                      v-bind="field"
                      :name="t('campaign_asset_set.sms_context')"
                      class="w-full rounded-md border p-2 duration-300 focus:border-cdp-black-500 disabled:text-cdp-grey-500"
                      :disabled="props.disabled"
                      rows="6"
                    />
                    <div
                      v-if="errors[t('campaign_asset_set.sms_context')]"
                      class="text-xs text-cdp-red-300"
                    >
                      {{ errors[t('campaign_asset_set.sms_context')] }}
                    </div>
                  </field>
                </div>
              </div>
            </div>
            <div class="flex w-full flex-wrap">
              <div class="w-full">
                <text-field
                  v-model="info.data.trackingUrl"
                  :disabled="props.disabled"
                  rules="url"
                  :name="t('campaign_asset_set.should_put_link_in_sms')"
                  :error-messages="errors[t('campaign_asset_set.should_put_link_in_sms')]"
                />
              </div>
            </div>
          </v-form>
          <div class="px-8">
            <slot name="footer" />
          </div>
        </div>

        <div class="flex w-full justify-center lg:w-1/3 xl:w-1/2">
          <div class="flex w-full justify-center py-4">
            <div class="relative md:w-[30vh]">
              <img :src="phoneImg" alt="phone" class="w-full" />
              <div
                class="absolute top-[11vh] h-[40vh] overflow-y-auto px-6 py-4 xl:w-[27vh]"
              >
                <div
                  v-show="info.data.text"
                  class="mb-4 w-fit rounded-md bg-gray-100 p-2 shadow-sm"
                >
                  <p class="break-all text-xs">
                    {{ info.data.text }}
                  </p>
                </div>

                <div
                  v-show="info.data.trackingUrl"
                  class="mb-4 w-fit rounded-md bg-gray-100 p-2 shadow-sm"
                >
                  <p class="break-all text-xs text-cdp-main-primary-dark underline">
                    {{ info.data.trackingUrl }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <modal v-if="warnModal" v-model="warnModal">
      <template #body>
        <div class="w-full p-4 lg:w-1/4">
          <div class="card-panel">
            <div class="relative p-6">
              <h3 class="mb-4 flex items-center gap-x-2 text-2xl">
                <icon name="ic_system_warning" class="h-6 w-6 text-cdp-yellow-400" />
                {{ t('campaign_asset_set.confirm_copy') }}
              </h3>
              <div class="text-left text-sm">
                {{ t('campaign_asset_set.conpy_warning') }}
              </div>
            </div>
            <div class="flex justify-end gap-3 p-5 pt-0">
              <button
                class="btn flex items-center border border-cdp-main-primary bg-cdp-main-primary-light text-cdp-main-primary hover:bg-cdp-primary-200"
                @click="warnModal = false"
              >
                {{ t('cancel') }}
              </button>
              <button
                class="btn bg-cdp-main-primary text-cdp-white hover:bg-cdp-main-primary-dark"
                @click="applyTemplate"
              >
                {{ t('copy') }}
              </button>
            </div>
          </div>
        </div>
      </template>
    </modal>

    <modal v-model="props.isSendPreview">
      <template #body>
        <div class="w-full p-4 lg:w-1/4">
          <div class="card-panel">
            <div class="relative p-6">
              <h3 class="mb-4 flex items-center gap-x-2 text-2xl">
                <icon name="ic_system_warning" class="h-6 w-6 text-cdp-yellow-400" />
                {{ t('campaign_asset_set.confirm_send') }}
              </h3>
              <div class="text-left text-sm">
                {{ t('campaign_asset_set.send_warning') }}
              </div>
            </div>
            <div class="flex justify-end gap-3 p-5 pt-0">
              <button
                class="btn flex items-center border border-cdp-main-primary bg-cdp-main-primary-light text-cdp-main-primary hover:bg-cdp-primary-200"
                @click="toCancel"
              >
                {{ t('cancel') }}
              </button>
              <button
                class="btn bg-cdp-yellow-400 text-cdp-white hover:bg-cdp-yellow-500"
                @click="sendPreview"
              >
                {{ t('send') }}
              </button>
            </div>
          </div>
        </div>
      </template>
    </modal>
  </div>
</template>

<script>
import {
  ApiSmsList,
  ApiSmsProfile,
  ApiSmsApplyTemplate,
  ApiSmsSendPreview
} from '@/api/sms.js';

import { useI18n } from 'vue-i18n';

import toast from '@/plugins/sweetalert2.js';

import { Field } from 'vee-validate';

import { storeToRefs } from 'pinia';
import { permissionStore } from '@/stores/permission.js';

import phoneImg from '@/assets/img/phone.png';

import modal from '@/components/Modal.vue';
import radio from '@/components/Radio.vue';

import {
  toRaw,
  ref,
  reactive,
  computed,
  watch,
  onBeforeMount,
  defineComponent
} from 'vue';
export default defineComponent({
  name: 'MarketingActivitiesSmsEdit',
  components: {
    field: Field,
    radio,
    modal
  },
  props: {
    uuid: {
      type: String,
      default: ''
    },
    keyword: {
      type: [Array, Object],
      default: () => []
    },
    isSendPreview: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  emits: ['change', 'update:isSendPreview'],
  setup(props, { emit }) {
    const { t } = useI18n();

    const storePermission = permissionStore();
    const { permission } = storeToRefs(storePermission);

    let smsForm = ref('');
    const useTemplate = ref('');
    const isUseTemplate = ref('no');

    const warnModal = ref(false);

    const info = reactive({
      data: {},
      smsList: []
    });

    const currentPermission = computed(() => {
      return toRaw(permission.value)['campaign_asset'];
    });

    watch(
      () => info.data,
      (data) => {
        emit('change', data);
      },
      { deep: true },
      { immediate: false }
    );

    const permissionInit = watch(
      () => currentPermission,
      (permission) => {
        if (permission.value?.retrieve) {
          smsInit();
          permissionInit();
        }
      },
      { deep: true },
      { immediate: false }
    );

    onBeforeMount(async () => {
      if (currentPermission.value?.retrieve) {
        smsInit();
      }
    });

    async function smsInit() {
      const data = await ApiSmsList({});
      if (!data.msg) {
        info.edmList = data.results.map((item) => ({
          text: item.subject,
          value: item.uuid
        }));
      }

      await getInfo();
    }

    async function getInfo() {
      const form = {
        uuid: props.uuid
      };
      const data = await ApiSmsProfile(form);
      if (!data.msg) {
        info.data = data;
      }
    }

    async function sendPreview() {
      const form = {
        uuid: props.uuid
      };
      const data = await ApiSmsSendPreview(form);
      if (!data.msg) {
        toast.fire({
          toast: true,
          icon: 'success',
          iconColor: 'green',
          text: t('send_success')
        });
        emit('update:isSendPreview', false);
      }
    }

    async function applyTemplate() {
      const form = {
        uuid: props.uuid,
        templateUuid: useTemplate.value
      };
      const data = await ApiSmsApplyTemplate(form);
      if (!data.msg) {
        toast.fire({
          toast: true,
          icon: 'success',
          iconColor: 'green',
          text: t('copy_success')
        });
        warnModal.value = false;
        useTemplate.value = '';
        isUseTemplate.value = 'no';
        getInfo();
      }
    }

    async function validate() {
      return await smsForm.value.validate();
    }

    function toCancel() {
      emit('update:isSendPreview', false);
    }

    return {
      t,
      props,
      phoneImg,
      smsForm,
      useTemplate,
      isUseTemplate,
      warnModal,
      info,
      sendPreview,
      applyTemplate,
      validate,
      toCancel
    };
  }
});
</script>